// Dashboard data cache + dedupe layer.
// Many dashboards request the same endpoint with identical filters
// (income/dashboard_montly, dashboard_daily, dashboard_location, sales_and_shop,
//  pending_balance_amount, dashboard_months_vs_last_moth, dashboard_hours, etc).
// This module memoizes by (endpoint + payload) with a short TTL and
// dedupes in-flight requests so simultaneous callers share one fetch.

const TTL_MS = 60 * 1000; // 60s — short enough that a refresh feels live
const buildKey = (endpoint, payload) =>
  endpoint + "::" + JSON.stringify(payload || {});

const state = {
  cache: Object.create(null),   // key -> { data, ts }
  inflight: Object.create(null) // key -> Promise<data>
};

const getters = {
  dashGet: (state) => (endpoint, payload) => {
    const entry = state.cache[buildKey(endpoint, payload)];
    if (!entry) return null;
    if (Date.now() - entry.ts > TTL_MS) return null;
    return entry.data;
  }
};

const actions = {
  /**
   * Fetch with cache + dedupe. The actual HTTP call is performed
   * by the caller via `fetcher` (so we don't need to import api_Post here).
   *   await dispatch('fetchCached', { endpoint, payload, fetcher })
   */
  async fetchCached({ state, commit }, { endpoint, payload, fetcher, force = false }) {
    const key = buildKey(endpoint, payload);
    const cached = state.cache[key];
    if (!force && cached && Date.now() - cached.ts < TTL_MS) {
      return cached.data;
    }
    if (state.inflight[key]) {
      return state.inflight[key];
    }
    const promise = (async () => {
      try {
        const data = await fetcher();
        commit("setCacheEntry", { key, data });
        return data;
      } finally {
        commit("clearInflight", key);
      }
    })();
    commit("setInflight", { key, promise });
    return promise;
  },

  invalidateAll({ commit }) {
    commit("resetCache");
  }
};

const mutations = {
  setCacheEntry(state, { key, data }) {
    state.cache = { ...state.cache, [key]: { data, ts: Date.now() } };
  },
  setInflight(state, { key, promise }) {
    state.inflight[key] = promise;
  },
  clearInflight(state, key) {
    delete state.inflight[key];
  },
  resetCache(state) {
    state.cache = Object.create(null);
    state.inflight = Object.create(null);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
